import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';
import {
  CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import { Input, Checkbox } from '../../common/Input';
import { useDropdownValues } from '../../hooks/useDropdownFetch';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
import { fetchDropdownTypesEnum, fetchDTRTuleConfurationTypesEnum } from "../../api/fetch";
import { transformEnumData } from "../../utils/textUtils";

// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // Added
import { Select } from "../../common/Select";

function DTRRulesConfiguration({ user, isHideSideMenu }) {
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();
  const [showInfo, setShowInfo] = useState(false); // State for floating info

  var headers = [
    "ID",
    "Priority Level",
    "Description",
    "Type",
    "DTR Rule Type",
    "Range From (min)",
    "Range To (min)",
    "Total Value (min)",
    "Selected Type",
    "Selected Value",
    "Status",
    "Actions"
  ];

  var accessors = [
    "id",
    "priority",
    "description",
    "type",
    "dtrRuleTypeName",
    "rangeFrom",
    "rangeTo",
    "totalValue",
    "dropdownTypeName",
    "dropdownValueName",
    "isActive",
    "actions"
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  const [dropdownTypeOptions, setDropdownTypeOptions] = useState([]);
  const [dTRRuleTypeOptions, setDTRRuleTypeOptions] = useState([]);
  const [enumsLoading, setEnumsLoading] = useState(false);

  const {
    dropdownValueOptions,
    dropdownValueLoading,
    fetchDropdownValues,
    resetDropdownValues,
    clearCacheForType
  } = useDropdownValues();

  const [form, setForm] = useState({
    description: "",
    type: "",
    dTRRuleType: "",
    rangeFrom: "",
    rangeTo: "",
    totalValue: "",
    priority: "1",
    dropdownType: "",
    dropdownValue: "",
    isActive: true,
  });

  const resetLocalVariables = () => {
    setForm({
      description: "",
      type: "",
      dTRRuleType: "",
      rangeFrom: "",
      rangeTo: "",
      totalValue: "",
      priority: "1",
      dropdownType: "",
      dropdownValue: "",
      isActive: true,
    });
    setSelectedData(null);
    resetDropdownValues();
  };

  const [filterData, setFilterData] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const apiUrl = domain + "/api/DTRRulesConfiguration";

  const fetchEnums = useCallback(async () => {
    try {
      setEnumsLoading(true);
      const dropdownRes = await fetchDropdownTypesEnum();
      const dTRRuleTypeRes = await fetchDTRTuleConfurationTypesEnum();
      console.log({dropdownRes, dTRRuleTypeRes});
      setDropdownTypeOptions(transformEnumData(dropdownRes));
      setDTRRuleTypeOptions(transformEnumData(dTRRuleTypeRes));
    } catch (error) {
      console.error('Error fetching enums:', error);
    } finally {
      setEnumsLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setData(response.data);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchEnums();
    fetchData();
  }, [fetchEnums, fetchData]);

  useEffect(() => {
    if (form.dropdownType) {
      fetchDropdownValues(form.dropdownType);
    } else {
      resetDropdownValues();
    }
  }, [form.dropdownType, fetchDropdownValues, resetDropdownValues]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    var res = filterData.filter((f) =>
      f?.description?.toString().toLowerCase().includes(lowercaseValue) ||
      f?.type?.toString().toLowerCase().includes(lowercaseValue) ||
      f?.dtrRuleTypeName?.toString().toLowerCase().includes(lowercaseValue) ||
      f?.priority?.toString().includes(lowercaseValue)
    );
    setData(res);
  };

  const handleAddSubmit = async (event) => {
    event.preventDefault();

    if (!form.description || !form.type || form.dTRRuleType === "" || form.dTRRuleType === null) {
      showGeneralMessage("Please fill in required fields (Description, Type, and DTR Rule Type)", false);
      return;
    }

    const apiData = {
      description: form.description.trim(),
      type: form.type.trim(),
      dTRRuleType: form.dTRRuleType !== "" ? parseInt(form.dTRRuleType) : null,
      rangeFrom: form.rangeFrom ? parseFloat(form.rangeFrom) : null,
      rangeTo: form.rangeTo ? parseFloat(form.rangeTo) : null,
      totalValue: form.totalValue ? parseFloat(form.totalValue) : null,
      priority: parseInt(form.priority) || null,
      dropdownType: form.dropdownType ? parseInt(form.dropdownType) : null,
      dropdownValue: form.dropdownValue ? parseInt(form.dropdownValue) : null,
      isActive: form.isActive
    };

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchData();
      showGeneralMessage("DTR rule created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      showGeneralMessage(error.response?.data?.message || "Failed to create DTR rule", false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdateSubmit = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) return;

    if (!form.description || !form.type || form.dTRRuleType === "" || form.dTRRuleType === null) {
      showGeneralMessage("Please fill in required fields", false);
      return;
    }

    const apiData = {
      description: form.description.trim(),
      type: form.type.trim(),
      dTRRuleType: form.dTRRuleType !== "" ? parseInt(form.dTRRuleType) : null,
      rangeFrom: form.rangeFrom ? parseFloat(form.rangeFrom) : null,
      rangeTo: form.rangeTo ? parseFloat(form.rangeTo) : null,
      totalValue: form.totalValue ? parseFloat(form.totalValue) : null,
      priority: parseInt(form.priority) || null,
      dropdownType: form.dropdownType ? parseInt(form.dropdownType) : null,
      dropdownValue: form.dropdownValue ? parseInt(form.dropdownValue) : null,
      isActive: form.isActive
    };

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchData();
      showGeneralMessage("DTR rule updated successfully!", true);
      closeEditHandleClick();
    } catch (error) {
      showGeneralMessage(error.response?.data?.message || "Failed to update DTR rule", false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);
    setForm({
      description: data.description || "",
      type: data.type || "",
      dTRRuleType: data.dtrRuleType !== undefined && data.dtrRuleType !== null ? data.dtrRuleType.toString() : "",
      rangeFrom: data.rangeFrom || "",
      rangeTo: data.rangeTo || "",
      totalValue: data.totalValue || "",
      priority: data.priority?.toString() || "1",
      dropdownType: data.dropdownType?.toString() || "",
      dropdownValue: data.dropdownValue?.toString() || "",
      isActive: data.isActive === true,
    });
    openEditHandleClick();
  };

  const openAddHandleClick = () => setShowAddForm(true);
  const closeAddHandleClick = () => { setShowAddForm(false); resetLocalVariables(); };
  const openEditHandleClick = () => setShowEditForm(true);
  const closeEditHandleClick = () => { setShowEditForm(false); resetLocalVariables(); };

  const actions = [
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="EmployeesTabInfo shadow-xl">
            <div className="employeesTab-header relative"> {/* Added relative for floating info */}
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Maintenance >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"DTR Rules Configuration"}</span>
              </div>

              <div className="flex justify-between items-center w-full">
                <h3>DTR Rules Configuration</h3>

                {/* Floating Info Toggle Button */}
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className="bg-transparent border-none cursor-pointer text-gray-400 hover:text-blue-500 transition-colors p-1"
                >
                  <InfoOutlinedIcon />
                </button>
              </div>

              {/* Floating Info Box */}
              {showInfo && (
                <div className="absolute right-4 top-16 z-50 w-80 bg-white border border-gray-200 shadow-2xl rounded-lg p-4 text-sm text-gray-600 animate-in fade-in zoom-in duration-200">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-gray-800">Configuration Guide</h4>
                    <CloseIcon fontSize="small" className="cursor-pointer hover:text-red-500" onClick={() => setShowInfo(false)} />
                  </div>
                  <p className="mb-2">This module defines how the system handles <strong>Night Differential</strong> and <strong>Undertime</strong> rounding.</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li><strong>Range:</strong> The actual minutes worked.</li>
                    <li><strong>Total Value:</strong> The standardized minutes to apply for payroll.</li>
                    <li><strong>Filtering:</strong> Use "Select Type" to apply rules only to specific Departments or Clients.</li>
                  </ul>
                </div>
              )}
            </div>

            <div className="employeesTab-body">
              <div className='p-4 space-y-4'>
                <SearchBar handleFilter={handleFilter} placeholder="Search rules..." />
                <Button
                  icon={<AddIcon />}
                  name={"Add DTR Rule"}
                  className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                  onClick={openAddHandleClick}
                />
                <CreateTable
                  tableName={"DTRRulesConfiguration"}
                  headers={headers}
                  accessors={accessors}
                  data={data}
                  actions={actions}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={data?.length}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddEditForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleAddSubmit}
        modalName={"Add DTR Rule"}
        mode="add"
        dropdownTypeOptions={dropdownTypeOptions}
        dropdownValueOptions={dropdownValueOptions}
        dTRRuleTypeOptions={dTRRuleTypeOptions}
        dropdownValueLoading={dropdownValueLoading}
        enumsLoading={enumsLoading}
        fetchDropdownValues={fetchDropdownValues}
        resetDropdownValues={resetDropdownValues}
        clearCacheForType={clearCacheForType}
      />

      <AddEditForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdateSubmit}
        modalName={"Update DTR Rule"}
        mode="edit"
        dropdownTypeOptions={dropdownTypeOptions}
        dropdownValueOptions={dropdownValueOptions}
        dTRRuleTypeOptions={dTRRuleTypeOptions}
        dropdownValueLoading={dropdownValueLoading}
        enumsLoading={enumsLoading}
        fetchDropdownValues={fetchDropdownValues}
        resetDropdownValues={resetDropdownValues}
        clearCacheForType={clearCacheForType}
      />

      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
      <LoadingSpinner loading={loadingRequest || loading || enumsLoading || dropdownValueLoading} />
    </div>
  );
}

function AddEditForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  mode = "add",
  dropdownTypeOptions,
  dropdownValueOptions,
  dTRRuleTypeOptions,
  dropdownValueLoading,
  enumsLoading,
  fetchDropdownValues,
  resetDropdownValues,
  clearCacheForType
}) {
  const submit = (e) => { e.preventDefault(); handleSubmit(e); };

  const handleInputChange = (field, value) => {
    if (field === 'dropdownType' && form.dropdownType !== value) {
      if (form.dropdownType && clearCacheForType) clearCacheForType(form.dropdownType);
      setForm(prev => ({ ...prev, [field]: value, dropdownValue: "" }));
      if (value && fetchDropdownValues) fetchDropdownValues(value);
      else if (resetDropdownValues) resetDropdownValues();
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const getDropdownLabel = () => {
    const dropdownOption = dropdownTypeOptions.find(opt => opt.value.toString() === form.dropdownType);
    return dropdownOption ? `${dropdownOption.label}` : 'Value';
  };

  if (!isOpen) return null;

  return (
    <div role="dialog" className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto">
      <div className="bg-gray-50 w-full md:w-[90%] lg:w-[50%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">
        <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800">{modalName}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 bg-transparent border-none cursor-pointer">
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-grow space-y-4">
          <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm pb-3">
            <h3 className="font-medium text-gray-700 py-2 border-b border-gray-300 mb-3 text-xs">Priority Rules</h3>
            <Input
              label="Priority Level"
              placeholder="Enter priority (e.g. 1)"
              value={form.priority}
              onChange={(e) => handleInputChange('priority', e.target.value)}
              type="number"
              isRequired={true}
            />
          </div>

          <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm pb-3">
            <h3 className="font-medium text-gray-700 py-2 border-b border-gray-300 mb-3 text-xs">Filtering (Optional)</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Select
                label="Select Maintenance Type"
                value={form.dropdownType}
                onChange={(e) => handleInputChange('dropdownType', e.target.value)}
                options={dropdownTypeOptions}
                isDisabled={enumsLoading}
              />
              {form.dropdownType && (
                <Select
                  label={getDropdownLabel()}
                  value={form.dropdownValue}
                  onChange={(e) => handleInputChange('dropdownValue', e.target.value)}
                  options={dropdownValueOptions}
                  isDisabled={dropdownValueLoading}
                />
              )}
            </div>
          </div>

          <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm pb-3">
            <h3 className="font-medium text-gray-700 py-2 border-b border-gray-300 mb-3 text-xs">Rule Configuration</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Input
                label="Description"
                placeholder="e.g. 15-min rounding"
                value={form.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                isRequired={true}
              />
              <Input
                label="Type"
                placeholder="e.g. Night Differential"
                value={form.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                isRequired={true}
              />
              <Select
                label="DTR Rule Type"
                value={form.dTRRuleType}
                onChange={(e) => handleInputChange('dTRRuleType', e.target.value)}
                options={dTRRuleTypeOptions}
                isRequired={true}
                isDisabled={enumsLoading}
              />
              <Input
                label="Range From (min)"
                placeholder="e.g. 0.00"
                value={form.rangeFrom}
                onChange={(e) => handleInputChange('rangeFrom', e.target.value)}
                type="number"
              />
              <Input
                label="Range To (min)"
                placeholder="e.g. 15.00"
                value={form.rangeTo}
                onChange={(e) => handleInputChange('rangeTo', e.target.value)}
                type="number"
              />
              <Input
                label="Total Value (min)"
                placeholder="e.g. 15.00"
                value={form.totalValue}
                onChange={(e) => handleInputChange('totalValue', e.target.value)}
                type="number"
              />
            </div>
          </div>

          <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm pb-3">
            <h3 className="font-medium text-gray-700 py-2 border-b border-gray-300 mb-3 text-xs">Status</h3>
            <Checkbox
              label="Active"
              checked={form.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white">
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer">Cancel</button>
          <button onClick={submit} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded border-none cursor-pointer" disabled={enumsLoading || dropdownValueLoading}>
            {mode === "edit" ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DTRRulesConfiguration;